<template>
  <v-card flat class="time-input-card">
    <v-card flat class="input-card pa-4">
      <v-chip-group v-model="mode" row mandatory class="timer-mode-chips mb-4">
        <v-chip value="stopwatch" outlined> ⏱ Stoppuhr </v-chip>
        <v-chip value="countdown" outlined> ⏳ Countdown </v-chip>
        <v-chip value="manual" outlined> 🔢 Manuell </v-chip>
      </v-chip-group>

      <div v-if="mode !== 'manual'" class="text-center timer-section">
        <div class="display-time-wrapper">
          <div class="display-time monospace" :class="{ running: running }">{{ formattedTime }}</div>
        </div>

        <v-text-field
          v-if="mode === 'countdown' && !running"
          v-model.number="countDownMinutes"
          label="Startzeit (Minuten)"
          type="number"
          dense
          inputmode="decimal"
          outlined
          hide-details
          class="mb-6 countdown-input" />

        <div class="controls-wrapper">
          <v-btn fab small color="rgba(255, 255, 255, 0.1)" @click="resetTimer" class="control-btn">
            <v-icon>mdi-restart</v-icon>
          </v-btn>
          <v-btn fab large :color="running ? 'red' : 'green'" @click="toggleTimer" class="mx-4 play-pause-btn">
            <v-icon size="36">{{ running ? "mdi-pause" : "mdi-play" }}</v-icon>
          </v-btn>
          <v-btn fab small color="rgba(255, 255, 255, 0.1)" @click="submit" :disabled="totalSeconds === 0" class="control-btn">
            <v-icon>mdi-check</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-else>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model.number="manualMinutes" inputmode="decimal" type="number" label="Minuten" outlined dense hide-details />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model.number="manualSeconds" inputmode="decimal" type="number" label="Sekunden" outlined dense hide-details />
          </v-col>
        </v-row>
      </div>

      <v-btn v-if="mode === 'manual'" block large class="mt-4 add-set-btn" color="primary" :disabled="totalSeconds === 0" @click="submit">
        Satz hinzufügen
      </v-btn>
    </v-card>
  </v-card>
</template>

<script>
export default {
  name: "TimeInput",
  props: ["exercise", "planSettings", "completedSets", "warmup", "completedSetsArray"],

  data() {
    return {
      mode: "stopwatch",
      milliseconds: 0,
      timer: null,
      running: false,
      startTime: null,
      countDownMinutes: 0,
      manualMinutes: 0,
      manualSeconds: 0,
    }
  },
  computed: {
    realSetsCompleted() {
      // Zählt alle Sätze, die nicht übersprungen wurden.
      return this.completedSetsArray.filter((set) => !set.skipped).length
    },
    warmupSetsCount() {
      // Prüft, ob ein Aufwärmsatz gemacht wurde.
      if (!this.warmup || this.completedSets === 0) return 0
      const warmupSet = this.completedSetsArray[0]
      return warmupSet && !warmupSet.skipped ? 1 : 0
    },
    targetSlideIndex() {
      // Berechnet den Index der nächsten Satzvorgabe.
      const totalSlides = (this.exercise?.sets?.length || 0) + (this.warmup ? 1 : 0)
      if (totalSlides === 0) return 0
      return Math.min(this.realSetsCompleted, totalSlides - 1)
    },
    totalSeconds() {
      if (this.mode === "manual") {
        return (this.manualMinutes || 0) * 60 + (this.manualSeconds || 0)
      }
      if (this.mode === "stopwatch") {
        return this.milliseconds / 1000
      }
      if (this.mode === "countdown") {
        // WICHTIG: Berechne die vergangene Zeit, nicht die verbleibende.
        const totalDurationInSeconds = (this.countDownMinutes || 0) * 60
        const remainingSeconds = this.milliseconds / 1000
        // Vergangene Zeit = Gesamtdauer - Verbleibende Zeit
        const elapsed = totalDurationInSeconds - remainingSeconds
        // Stelle sicher, dass wir keinen negativen Wert zurückgeben, falls es eine kleine Verzögerung gibt
        return Math.max(0, elapsed)
      }
      return 0
    },
    formattedTime() {
      const totalSeconds = Math.floor(this.milliseconds / 1000)
      const min = String(Math.floor(totalSeconds / 60)).padStart(2, "0")
      const sec = String(totalSeconds % 60).padStart(2, "0")
      const hund = String(Math.floor((this.milliseconds % 1000) / 10)).padStart(2, "0")
      return `${min}:${sec}:${hund}`
    },
  },
  methods: {
    initDefaults() {
      const plannedDurationSetting = this.planSettings?.find((s) => s.text === "Dauer")
      if (plannedDurationSetting) {
        const value = Number(plannedDurationSetting.value)
        const unit = plannedDurationSetting.unit || "min" // Fallback auf Minuten

        if (unit === "min") {
          this.countDownMinutes = value
        } else {
          // 'sek'
          // In Minuten umrechnen, da die interne Logik damit arbeitet
          this.countDownMinutes = value / 60
        }
      } else {
        this.countDownMinutes = 1 // Fallback auf 1 Minute
      }
      // Setze den initialen Zustand basierend auf dem Modus
      this.resetTimer()
    },
    toggleTimer() {
      if (this.running) {
        this.pauseTimer()
      } else {
        this.startTimer()
      }
    },
    startTimer() {
      if (this.running) return

      // Für Countdown: Wenn der Timer bei 0 ist, setze ihn auf die Startzeit zurück.
      if (this.mode === "countdown" && this.milliseconds === 0) {
        this.milliseconds = this.countDownMinutes * 60 * 1000
      }

      this.running = true
      this.startTime = Date.now()

      // Merke dir den Millisekunden-Stand beim Start, um Pausen korrekt zu behandeln
      const startingMilliseconds = this.milliseconds

      this.timer = setInterval(() => {
        const elapsedSinceStart = Date.now() - this.startTime
        if (this.mode === "stopwatch") {
          this.milliseconds = startingMilliseconds + elapsedSinceStart
        } else {
          // Countdown
          this.milliseconds = Math.max(0, startingMilliseconds - elapsedSinceStart)
          if (this.milliseconds === 0) {
            this.pauseTimer()
            // Optional: Sound/Vibration abspielen
          }
        }
      }, 51) // Hohe Frequenz für flüssige Hundertstel
    },
    pauseTimer() {
      clearInterval(this.timer)
      this.running = false
    },
    resetTimer() {
      this.pauseTimer()
      // Stoppuhr auf 0, Countdown auf die eingestellte Minutenzahl
      this.milliseconds = this.mode === "countdown" ? (this.countDownMinutes || 0) * 60 * 1000 : 0
    },
    submit() {
      this.pauseTimer()
      const value = this.totalSeconds
      // Nur Sätze hinzufügen, die eine Dauer > 0 haben
      if (value > 0) {
        // Wir speichern die Dauer in Sekunden für eine einfachere Weiterverarbeitung
        this.$emit("add-set", [{ text: "Dauer", value: value.toFixed(2) }])
        this.initDefaults() // Setzt den Timer für den nächsten Satz zurück
        this.manualMinutes = 0
        this.manualSeconds = 0
      }
    },
  },
  watch: {
    mode(newVal) {
      this.resetTimer()
    },
    completedSets: {
      handler() {
        this.initDefaults()
      },
    },
    // Beobachte Änderungen an den Minuten, um die Anzeige zu aktualisieren
    countDownMinutes() {
      if (this.mode === "countdown" && !this.running) {
        this.resetTimer()
      }
    },
    planSettings: {
      handler() {
        this.initDefaults()
      },
      immediate: true,
      deep: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
}
</script>

<style scoped>
.time-input-card {
  background: transparent !important;
}
.input-card {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
}

.timer-mode-chips .v-chip {
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.05);
}
.timer-mode-chips .v-chip--active {
  border-color: var(--v-primary-base);
  background: rgba(var(--v-primary-base-rgb), 0.2);
}

.timer-section {
  padding: 16px 0;
}

.display-time-wrapper {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 8px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
}

.display-time {
  font-size: 3.5rem;
  font-weight: 700;
  color: #ffffff;
  background: transparent;
  padding: 16px;
  font-family: 'Roboto Mono', monospace;
  letter-spacing: 2px;
  transition: all 0.3s ease;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
}

.display-time.running {
  color: var(--v-primary-base);
  text-shadow: 0 0 20px rgba(var(--v-primary-base-rgb), 0.6);
}

.controls-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

.control-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(4px);
}

.play-pause-btn {
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.4);
  /* Gradient wird über inline style oder color prop gesteuert, hier nur der Shadow */
}

.countdown-input {
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
}

.add-set-btn {
  height: 56px !important;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  width: 100%;
  box-shadow: 0 8px 20px rgba(0,0,0,0.3);
  background: linear-gradient(135deg, var(--v-primary-base), rgba(var(--v-primary-base-rgb), 0.6)) !important;
  border: 1px solid rgba(255,255,255,0.1);
}
</style>
